/* eslint-disable no-magic-numbers */
import React from 'react'

export type BreakpointName =
  | 'mobile'
  | 'tabletPortrait'
  | 'tabletLandscape'
  | 'desktop'

type MediaValueConfig = Record<BreakpointName, number>

/**
 * Minimum size for that breakpoint to be active
 */
export const breakpointsMinSize: MediaValueConfig = {
  mobile: 0,
  tabletPortrait: 768,
  tabletLandscape: 1024,
  desktop: 1440,
}

/**
 * Maximum size for that breakpoint to be active
 */
export const breakpointsMaxSize: MediaValueConfig = {
  mobile: breakpointsMinSize.tabletPortrait - 1,
  tabletPortrait: breakpointsMinSize.tabletLandscape - 1,
  tabletLandscape: breakpointsMinSize.desktop - 1,
  // setting to Infinity messes with matchMedia queries
  // getMediaQueryString would have to check and adjust the string
  desktop: breakpointsMinSize.desktop + 10000,
}

type BreakpointMediaQueryList = Record<BreakpointName, string>

export const defaultMediaConfig = {
  mobile: `(width <= ${breakpointsMaxSize.mobile}px)`,
  tabletPortrait: `(${breakpointsMinSize.tabletPortrait}px <= width <= ${breakpointsMaxSize.tabletPortrait}px)`,
  tabletLandscape: `(${breakpointsMinSize.tabletLandscape}px <= width <= ${breakpointsMaxSize.tabletLandscape}px)`,
  desktop: `(min-width: ${breakpointsMinSize.desktop}px)`,
}

/**
 * Point in time check if matching a specific breakpoint
 */
export function getCurrentMedia(
  config: BreakpointMediaQueryList = defaultMediaConfig
) {
  const isMobile = window.matchMedia(config.mobile).matches
  const isTabletPortrait = window.matchMedia(config.tabletPortrait).matches
  const isTabletLandscape = window.matchMedia(config.tabletLandscape).matches
  const isDesktop = window.matchMedia(config.desktop).matches

  return {
    isMobile,
    isTabletPortrait,
    isTabletLandscape,
    isDesktop,
  }
}

/**
 * Reactive check if matching a specific breakpoint
 */
export function useCurrentMedia() {
  const [currentMedia, setCurrentMedia] = React.useState(getCurrentMedia())

  React.useEffect(() => {
    function handleResize() {
      setCurrentMedia(getCurrentMedia())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return currentMedia
}
